/**
 * Webtech HR Engine™ - Compétences Équipe Manager
 * Affiche les compétences de l'équipe du manager
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Brain,
  Users,
  TrendingUp,
  TrendingDown,
  Star,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  BarChart3,
  Target
} from 'lucide-react';

export default function TeamSkillsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamSkills, setTeamSkills] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, [user?.companyId]);
  
  const fetchData = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    
    try {
      const [teamRes, skillsRes] = await Promise.all([
        api.get('/manager/team').catch(() => ({ data: { members: [] } })),
        api.get('/manager/team/skills').catch(() => ({ data: { skills: [] } }))
      ]);
      
      setTeamMembers(teamRes.data?.members || []);
      setTeamSkills(skillsRes.data?.skills || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Données actualisées');
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-600';
  };
  
  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <DashboardLayout title="Compétences de mon équipe">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compétences de mon équipe</h1>
            <p className="text-slate-500 mt-1">
              Vue d'ensemble des compétences de vos {teamMembers.length} collaborateur(s)
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{teamMembers.length}</p>
                  <p className="text-sm text-slate-500">Collaborateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {teamMembers.filter(m => m.performanceScore !== null && m.performanceScore !== undefined).length}
                  </p>
                  <p className="text-sm text-slate-500">Évalués</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {teamMembers.filter(m => (m.performanceScore || 0) >= 70).length}
                  </p>
                  <p className="text-sm text-slate-500">Performants (≥70%)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {teamMembers.filter(m => (m.performanceScore || 0) > 0 && (m.performanceScore || 0) < 50).length}
                  </p>
                  <p className="text-sm text-slate-500">À accompagner</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Team Skills Overview */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Performance Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Performance de l'équipe
              </CardTitle>
              <CardDescription>
                Score moyen : {teamMembers.length > 0 
                  ? (teamMembers.reduce((sum, m) => sum + (m.performanceScore || 0), 0) / teamMembers.length).toFixed(1)
                  : 0}%
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : teamMembers.filter(m => m.performanceScore).length > 0 ? (
                <div className="space-y-4">
                  {teamMembers.filter(m => m.performanceScore !== null && m.performanceScore !== undefined).map((member) => (
                    <div key={member.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={member.photoUrl} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                              {member.firstName?.[0]}{member.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium text-slate-700">
                            {member.firstName} {member.lastName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${getScoreColor(member.performanceScore)}`}>
                            {member.performanceScore}%
                          </span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              member.performanceScore >= 70 ? 'border-green-200 text-green-600' :
                              member.performanceScore >= 50 ? 'border-blue-200 text-blue-600' :
                              'border-amber-200 text-amber-600'
                            }`}
                          >
                            {member.performanceScore >= 70 ? 'Excellent' :
                             member.performanceScore >= 50 ? 'Satisfaisant' : 'À améliorer'}
                          </Badge>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(member.performanceScore)} transition-all`}
                          style={{ width: `${member.performanceScore}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Aucune évaluation complétée</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Lancez une campagne d'évaluation pour commencer
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Team Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Mon équipe
              </CardTitle>
              <CardDescription>
                Niveau de compétences par collaborateur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : teamMembers.length > 0 ? (
                <div className="space-y-3">
                  {teamMembers.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-colors"
                      onClick={() => navigate(`/skills/employee/${member.id}`)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={member.photoUrl} />
                        <AvatarFallback className="bg-blue-100 text-blue-700">
                          {member.firstName?.[0]}{member.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-sm text-slate-500">{member.jobTitle}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${getScoreColor(member.performanceScore || member.skillsScore || 0)}`}>
                          {member.performanceScore || member.skillsScore || 0}%
                        </p>
                        <p className="text-xs text-slate-400">Score performance</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Aucun collaborateur dans votre équipe</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Skills Gaps Section */}
        {teamSkills.filter(s => s.gap > 0).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Écarts à combler
              </CardTitle>
              <CardDescription>
                Compétences où l'équipe est en dessous du niveau requis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {teamSkills.filter(s => s.gap > 0).map((skill) => (
                  <div 
                    key={skill.id || skill.name}
                    className={`p-4 rounded-lg border-l-4 ${
                      skill.status === 'Critique' 
                        ? 'bg-red-50 border-l-red-500' 
                        : 'bg-amber-50 border-l-amber-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-900">{skill.name}</span>
                      <Badge 
                        variant="outline" 
                        className={skill.status === 'Critique' ? 'border-red-200 text-red-600' : 'border-amber-200 text-amber-600'}
                      >
                        {skill.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-500">Actuel:</span>
                      <span className="font-semibold">{skill.avgLevel?.toFixed(1)}/5</span>
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      <span className="text-slate-500">Requis:</span>
                      <span className="font-semibold">{(skill.avgLevel + skill.gap)?.toFixed(1)}/5</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
